import React, { useEffect, useMemo, useState } from 'react'
import { Render, type Config, type Metadata } from '@puckeditor/core'
import { resolveResponsive } from './resolve'
import {
  BreakpointId,
  DEFAULT_BREAKPOINT,
  breakpointForWidth,
} from './breakpoints'
import type { ResponsiveData } from './types'

export interface ResponsiveRenderProps {
  config: Config
  data: ResponsiveData
  metadata?: Metadata
}

/**
 * Production renderer for responsive puck steps. SSR / first paint uses the
 * DEFAULT view; on mount it resolves to the breakpoint matching the device
 * width and re-resolves only when a breakpoint boundary is crossed. Components
 * are unchanged — they receive already-resolved props.
 */
export const ResponsiveRender = ({
  config,
  data,
  metadata,
}: ResponsiveRenderProps) => {
  const [bp, setBp] = useState<BreakpointId>(DEFAULT_BREAKPOINT)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const sync = () => {
      const next = breakpointForWidth(window.innerWidth)
      // setState bails out when unchanged → only re-renders on boundary cross
      setBp(prev => (prev === next ? prev : next))
    }
    sync()
    window.addEventListener('resize', sync)
    return () => window.removeEventListener('resize', sync)
  }, [])

  const resolved = useMemo(() => resolveResponsive(data, bp), [data, bp])

  return <Render config={config} data={resolved} metadata={metadata} />
}
